/**
 * Merchant Pro V1 PR-4b — Notification slide-over.
 *
 * Right-anchored drawer with:
 *   • Header (title + close)
 *   • Filter chips (All / Reservations / Waitlist / System)
 *   • Mark-all-read button
 *   • Recent 7-day feed (rows with per-row mark-read + deep-link)
 *   • Empty state when no rows match the filter
 *
 * Polls /api/owner/notifications every 30s while visible.
 * Polls /api/owner/notifications?limit=1 (bell) every 15s independently.
 *
 * Operator-locked: polling only. NO push/SSE/WS in PR-4b.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  X, CheckCircle2, MailOpen, ExternalLink, CalendarDays, ListChecks, Settings as SettingsIcon, Inbox,
} from 'lucide-react';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';

const CATEGORY_CHIPS = [
  { key: null,           label: 'All',          icon: Inbox },
  { key: 'reservation',  label: 'Reservations', icon: CalendarDays },
  { key: 'waitlist',     label: 'Waitlist',     icon: ListChecks },
  { key: 'system',       label: 'System',       icon: SettingsIcon },
];

function _relativeTime(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function NotificationSlideOver({ open, onClose }) {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [category, setCategory] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: qk.ownerNotifications({ category, lookback_days: 7 }),
    queryFn: () => api.ownerNotifications(token, {
      category: category || undefined,
      lookback_days: 7,
      limit: 50,
    }),
    enabled: !!token && !!open,
    refetchInterval: open ? 30_000 : false,
    refetchOnWindowFocus: true,
  });

  const items = useMemo(() => data?.items ?? [], [data]);
  const unread = data?.unread_count ?? 0;

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['owner', 'notifications'] });
  };

  const markReadM = useMutation({
    mutationFn: (id) => api.ownerNotificationsMarkRead(token, { notification_ids: [id] }),
    onSuccess: invalidateAll,
  });
  const markAllM = useMutation({
    mutationFn: () => api.ownerNotificationsMarkRead(token, { mark_all: true }),
    onSuccess: invalidateAll,
  });

  const onRowClick = async (row) => {
    if (!row.is_read) {
      try { await markReadM.mutateAsync(row.id); } catch { /* swallow */ }
    }
    if (row.target_url) navigate(row.target_url);
    onClose?.();
  };

  if (!open) return null;

  return (
    <div
      data-testid="notif-slide-over-overlay"
      className="fixed inset-0 z-40 flex justify-end bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <aside
        data-testid="notif-slide-over"
        className="w-full max-w-md h-full bg-[#0E0E13] border-l border-white/10 shadow-2xl flex flex-col"
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40 font-semibold">
              Notifications · Last 7 days
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <h2 className="text-lg font-semibold" data-testid="notif-slide-over-title">
                Inbox
              </h2>
              {unread > 0 ? (
                <span
                  data-testid="notif-slide-over-unread-pill"
                  className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                  style={{ background: '#D4AF3722', color: '#D4AF37', border: '1px solid #D4AF3755' }}
                >
                  {unread} unread
                </span>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="notif-slide-over-close"
            className="p-2 rounded-full text-white/70 hover:bg-white/5"
          >
            <X size={18} />
          </button>
        </header>

        {/* Filter chips */}
        <div className="px-6 py-3 border-b border-white/5 flex items-center justify-between gap-3">
          <div
            data-testid="notif-slide-over-filter-chips"
            className="flex flex-wrap gap-2"
          >
            {CATEGORY_CHIPS.map((c) => {
              const Icon = c.icon;
              const active = (category ?? null) === (c.key ?? null);
              return (
                <button
                  key={c.label}
                  type="button"
                  onClick={() => setCategory(c.key)}
                  data-testid={`notif-chip-${c.key ?? 'all'}`}
                  data-active={active ? 'true' : 'false'}
                  className={
                    'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ' +
                    (active
                      ? 'bg-gold/15 text-gold border-gold/40'
                      : 'bg-[#15151B] text-white/65 border-white/10 hover:bg-white/5')
                  }
                >
                  <Icon size={12} />
                  {c.label}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => markAllM.mutate()}
            disabled={markAllM.isPending || unread === 0}
            data-testid="notif-slide-over-mark-all"
            className="text-xs font-semibold text-white/70 hover:text-white disabled:opacity-40 flex items-center gap-1"
          >
            <MailOpen size={12} />
            Mark all read
          </button>
        </div>

        {/* Feed */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="text-white/50 text-sm py-12 text-center" data-testid="notif-slide-over-loading">
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div
              data-testid="notif-slide-over-empty"
              className="text-white/40 text-sm py-12 text-center px-6"
            >
              <Inbox size={28} className="mx-auto mb-2 opacity-60" />
              <div className="font-semibold">No notifications</div>
              <div className="mt-1">Operational events for the last 7 days will surface here.</div>
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {items.map((row) => (
                <NotificationRow
                  key={row.id}
                  row={row}
                  onClick={() => onRowClick(row)}
                  onMarkRead={() => markReadM.mutate(row.id)}
                  isPending={markReadM.isPending}
                />
              ))}
            </ul>
          )}
        </div>

        <footer className="px-6 py-3 border-t border-white/10 text-center">
          <button
            type="button"
            onClick={() => { navigate('/owner/notifications'); onClose?.(); }}
            data-testid="notif-slide-over-view-all"
            className="text-xs font-semibold text-gold hover:text-gold/85"
          >
            View all notifications →
          </button>
        </footer>
      </aside>
    </div>
  );
}

function NotificationRow({ row, onClick, onMarkRead, isPending }) {
  const catColor = {
    reservation: '#FBBF24',
    waitlist:    '#3B82F6',
    system:      '#F97316',
  }[row.category] || '#FFFFFF';

  return (
    <li
      data-testid={`notif-row-${row.id}`}
      data-category={row.category}
      data-is-read={row.is_read ? 'true' : 'false'}
      className={
        'group flex items-start gap-3 px-6 py-3 hover:bg-white/[0.03] transition-colors cursor-pointer ' +
        (row.is_read ? 'opacity-65' : '')
      }
      onClick={onClick}
    >
      {/* Unread dot */}
      <span
        className="mt-1.5 w-2 h-2 rounded-full shrink-0"
        style={{ background: row.is_read ? 'transparent' : '#D4AF37' }}
        aria-hidden
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span
            className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-md"
            style={{ background: catColor + '22', color: catColor, border: `1px solid ${catColor}55` }}
          >
            {row.category}
          </span>
          <span className="text-[10px] text-white/40 font-mono">{_relativeTime(row.created_at)}</span>
        </div>
        <div className="text-sm font-medium text-white/95 mt-1 leading-snug">{row.title}</div>
        {row.body ? (
          <div className="text-xs text-white/55 mt-0.5 truncate">{row.body}</div>
        ) : null}
      </div>
      <div className="flex items-center gap-1 shrink-0 self-center opacity-0 group-hover:opacity-100 transition-opacity">
        {!row.is_read ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onMarkRead(); }}
            disabled={isPending}
            data-testid={`notif-row-${row.id}-mark-read`}
            className="p-1.5 rounded-full text-white/70 hover:bg-white/5"
            title="Mark as read"
          >
            <CheckCircle2 size={14} />
          </button>
        ) : null}
        {row.target_url ? (
          <span className="p-1.5 rounded-full text-white/50">
            <ExternalLink size={12} />
          </span>
        ) : null}
      </div>
    </li>
  );
}
